/**
 * The design system, transcribed from `UI design and authentication/Kilowatt
 * Estimator App.html`. One frozen object, imported by every component; nothing
 * in this package writes a colour, a weight or a radius as a literal.
 *
 * **The palette is a contract with the brand, not a preference.** The six
 * brand colours are APS's and `tokens.test.ts` pins each one exactly, so a
 * "nicer" navy is a failing test rather than a diff nobody reads.
 *
 * **The shape language is deliberately square.** Panels, cards, inputs and
 * segmented controls are `border-radius: 0`. Only pills (999px) and buttons
 * (3px) are rounded — the mock uses exactly those two values and no others —
 * and *borders do the work shadows normally would*: 1.5px `rule` between
 * sections, 1px `hairline` inside them, 4px inset left rails to colour-code
 * sidebar cards. **Do not add shadows.** There is no shadow token here and
 * that absence is the design decision.
 *
 * Every value below was read back out of the mock rather than remembered:
 * all sixteen `color` and ten `tint` hexes occur in it, the rainbow gradient
 * is byte-identical to the four copies it carries, `3px`/`999px` are its only
 * two radii, `0.12em` is its most common tracking (33 uses) and pairs with
 * `10px` (67 uses) on every micro-label, and 272/336/380 are its three rail
 * widths.
 */

/**
 * The five Archivo weights the mock actually ships `@font-face` rules for:
 * 400, 500, 600, 700 and 800. Nothing here is synthesised by the browser.
 *
 * Plan 4's Task 9 text lists only three (400/600/800) and its sample test
 * asserts `not.toContain(700)`. That is wrong about the mock, which declares
 * three `@font-face` rules at 700 and uses `font-weight: 700` fifty-four
 * times — more than any weight except 600. The plan's own "things the mock
 * gets wrong" section already retracts the claim ("This one was my error, not
 * the mock's … use whatever weight the mock uses"); the retraction just never
 * reached the task body. See task-9-report.md.
 */
const weight = { body: 400, medium: 500, semi: 600, bold: 700, display: 800 } as const

export const T = {
  color: {
    navy: '#005993', red: '#EE2A24', green: '#009851',
    amber: '#FBB040', cyan: '#00BDDF', lime: '#9CCB44',
    money: '#00714a',        // darker than brand green; used only for currency
    undetermined: '#a05a00', // the "someone looked and could not tell" amber
    ink: '#201e1d', body: '#4a4642', muted: '#6d7b86', micro: '#8a847e',
    border: '#b9c4cc', rule: '#d5dde3', hairline: '#eaeef1', chipOff: '#cfd8de',
    outOfWindow: '#a8a29b',
    // Label and secondary text *on* the navy panel — the sign-in screen's
    // right half. `muted` is tuned for ink on white and disappears on navy.
    onNavy: '#9fc4dc',
    // Ink for a warning box on the `stale` tint. Warm and dark enough to read
    // at 13px against #fff5e6, which brand amber is not.
    warnInk: '#7a4a00',
    // The four inks and borders the mock's pill helpers use — `confStyleFor`
    // and `statusStyleFor`, read out of the export rather than remembered.
    // Each is a foreground on one specific background and is not a shade of
    // anything already here: `onAmber` is text on the brand amber (white and
    // `ink` both fail it), `enrolledInk` and `declinedInk` are text on the
    // `enrolled` and `red` tints, and `dashedBorder` is the dashed outline of
    // the Low-confidence pill, which is deliberately quieter than `border`.
    onAmber: '#3d2300',
    enrolledInk: '#00603a',
    declinedInk: '#9e1c17',
    dashedBorder: '#a8b4bd',
    // The profile chart's own three greys and blues, read out of the mock's
    // `chartEl`. None is a shade of anything already here and none is reusable
    // elsewhere: the two tick colours are 1px hairlines drawn *on top of* the
    // `chartWindow` tint and the white plot respectively, and each is tuned to
    // that background, while `chartFootnote` is one step lighter than `micro`
    // because the footnote is a legend and must not compete with the hour
    // labels beside it. They live here rather than as literals in the chart so
    // that `tokens.ts` stays the only place this package writes a colour.
    chartTickIn: '#b6dfec',
    chartTickOut: '#e8e5e1',
    chartFootnote: '#9a948e',
  },
  tint: {
    blue: '#f2fafd', green: '#f2faf5', rowHover: '#e7eef3', enrolled: '#eaf7f0',
    red: '#fdeceb', stale: '#fff5e6', chartWindow: '#e7f6fb', chartDropped: '#f4f1ec',
    base: '#eef6e0', rescore: '#eceae7',
  },
  /** `Archivo` first, then the platform's own UI face — exactly the stack the mock declares. */
  fontFamily: 'Archivo, system-ui, sans-serif',
  weight,
  /** The brand signature, on every screen. Byte-identical to the mock's. */
  rainbow: 'linear-gradient(90deg,#FBB040,#EE2A24 22%,#9CCB44 44%,#009851 60%,#00BDDF 80%,#005993)',
  /** Height of the rainbow rule at the top of every screen. */
  rainbowHeight: 5,
  rail: { filters: 272, account: 336, presentation: 380 },
  /**
   * Micro-labels: uppercase with wide tracking, on every section label and
   * every pill.
   *
   * **12px, and that is a deliberate departure from the mock, which uses 10px
   * in 67 places.** The mock was drawn before the body copy around these labels
   * grew: section titles at 10px above 14.5px paragraphs read as captions
   * rather than as headings, and a rep reported them as too small to scan. The
   * uppercase transform and the 0.12em tracking are what make these labels
   * micro-labels; the size was carrying that job as well, and 12px still sits
   * far enough below the 14.5px body to keep the hierarchy.
   *
   * One token rather than 39 edits, so titles and pills stay in proportion to
   * each other. If this ever needs to go back, it is this line.
   */
  micro: { size: 12, tracking: '0.12em', transform: 'uppercase' },
  radius: { square: 0, button: 3, pill: 999 },
} as const

export type Tokens = typeof T
